/**
 * Centralized RBAC & Permission Utility
 * DMRC HRMS Security Enhancement (CWE-285 / OWASP Broken Access Control)
 * 
 * IMPORTANT:
 * Frontend permission checks are defense-in-depth UI controls only.
 * Authoritative authorization MUST be strictly validated on the backend for every API endpoint.
 */

export interface UserSessionData {
  id?: string | number;
  employee_id?: string;
  empId?: string;
  username?: string;
  email?: string;
  role?: string;
  user_type?: string;
  hrms_role?: any;
  [key: string]: any;
}

/**
 * Get current user object safely from browser session
 */
export const getCurrentUser = (): UserSessionData | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem("user");
    if (!raw || raw === "undefined" || raw === "null") return null;
    return JSON.parse(raw);
  } catch (err) {
    console.error("Error parsing user session:", err);
    return null;
  }
};

/**
 * Get current user's role safely
 */
export const getUserRole = (): string => {
  const user = getCurrentUser();
  if (!user) return "";
  const roleObj: any = user.role;
  const rawRole =
    (typeof roleObj === "object" && roleObj !== null ? roleObj.role_name : roleObj) ||
    user.user_type ||
    "";
  return String(rawRole || "").toUpperCase().trim();
};

/**
 * Get current user's employee ID
 */
export const getCurrentEmployeeId = (): string | null => {
  const user = getCurrentUser();
  if (!user) return null;
  return user.employee_id || user.empId || (user.id ? String(user.id) : null);
};

/**
 * Get cached allowed permissions list
 */
export const getUserPermissions = (): string[] => {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem("allowed_permissions");
    if (!raw || raw === "undefined" || raw === "null") return [];
    const perms = JSON.parse(raw);
    return Array.isArray(perms) ? perms : [];
  } catch {
    return [];
  }
};

/**
 * Helper to check if role is Admin, SuperAdmin, or SubAdmin (Full access)
 */
export const isAdminOrSubAdmin = (userRole?: string): boolean => {
  const role = (userRole || getUserRole()).toUpperCase().replace(/[-_ ]/g, "");
  return ["ADMIN", "SUPERADMIN", "SUBADMIN", "HRADMIN"].includes(role);
};

/**
 * Helper to check if role is SUPPORT-VIEWER (View & Sync actions only)
 */
export const isSupportViewer = (userRole?: string): boolean => {
  const rawRole = (userRole || getUserRole()).toUpperCase().trim();
  const normalizedRole = rawRole.replace(/[-_ ]/g, "");
  return (
    rawRole === "SUPPORT-VIEWER" ||
    rawRole === "SUPPORT_VIEWER" ||
    normalizedRole === "SUPPORTVIEWER" ||
    normalizedRole === "SUPPORT" ||
    normalizedRole === "VIEWER"
  );
};

/**
 * Check if the user has a specific permission code
 */
export const hasPermission = (
  permissionCode: string,
  userPermissions?: string[],
  userRole?: string
): boolean => {
  const role = userRole || getUserRole();
  if (isAdminOrSubAdmin(role)) {
    return true;
  }
  if (isSupportViewer(role)) {
    // Support Viewer can only access sync operations or general view permissions
    if (permissionCode && permissionCode.toLowerCase().includes("sync")) {
      return true;
    }
    return false;
  }

  const permissions = userPermissions || getUserPermissions();
  if (!permissionCode) return true;
  return permissions.includes(permissionCode);
};

/**
 * Check if the user has AT LEAST ONE of the specified permission codes
 */
export const hasAnyPermission = (
  permissionCodes: string[],
  userPermissions?: string[],
  userRole?: string
): boolean => {
  const role = userRole || getUserRole();
  if (isAdminOrSubAdmin(role)) {
    return true;
  }
  if (isSupportViewer(role)) {
    return permissionCodes.some((code) => code.toLowerCase().includes("sync"));
  }

  const permissions = userPermissions || getUserPermissions();
  if (!permissionCodes || permissionCodes.length === 0) return true;
  return permissionCodes.some((code) => permissions.includes(code));
};

/**
 * Check if the user has ALL of the specified permission codes
 */
export const hasAllPermissions = (
  permissionCodes: string[],
  userPermissions?: string[],
  userRole?: string
): boolean => {
  const role = userRole || getUserRole();
  if (isAdminOrSubAdmin(role)) {
    return true;
  }
  if (isSupportViewer(role)) {
    return permissionCodes.every((code) => code.toLowerCase().includes("sync"));
  }

  const permissions = userPermissions || getUserPermissions();
  if (!permissionCodes || permissionCodes.length === 0) return true;
  return permissionCodes.every((code) => permissions.includes(code));
};

/**
 * Check if the user matches one or more allowed roles
 */
export const hasRole = (
  requiredRoles: string | string[],
  userRole?: string
): boolean => {
  const currentRole = (userRole || getUserRole()).toUpperCase();
  if (!currentRole) return false;

  const roles = Array.isArray(requiredRoles)
    ? requiredRoles.map((r) => r.toUpperCase())
    : [requiredRoles.toUpperCase()];

  return roles.includes(currentRole);
};
